import { FC } from "react";
import { Theme, Stack } from "@mui/material";
import { Formik, FormikHelpers } from "formik";
import { Modal, ModalProps } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import UserFilter from "../../../components/forms/Reports/UserFilter";
import MileageObjectFilter from "../../../components/forms/Reports/MileageObjectFilter";

export interface IMileageDeviationFilter {
  userUniqueId: string;
  objectUniqueId: string;
}

export type MileageDeviationFilterModalProps = Pick<
  ModalProps,
  "open" | "onClose"
> & {
  onSubmit: (
    values: IMileageDeviationFilter,
    formikHelpers: FormikHelpers<IMileageDeviationFilter>
  ) => void;
  theme: Theme;
  submitting?: boolean;
  initialValues?: IMileageDeviationFilter;
  hasActiveFilters?: boolean;
};

const initialFormValues: IMileageDeviationFilter = {
  userUniqueId: "",
  objectUniqueId: ""
};

const validationSchema = Yup.object().shape({
  userUniqueId: Yup.string().nullable(),
  objectUniqueId: Yup.string().nullable()
});

const MileageDeviationFilterModal: FC<MileageDeviationFilterModalProps> = ({
  open,
  onClose,
  onSubmit,
  theme,
  submitting = false,
  initialValues,
  hasActiveFilters = false
}) => {
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);

  return (
    <Formik
      initialValues={initialValues || initialFormValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ handleSubmit, isSubmitting, resetForm, values, setFieldValue }) => {
        const isFormEmpty = !values.userUniqueId && !values.objectUniqueId;
        const shouldDisableApply = isFormEmpty && !hasActiveFilters;

        return (
          <Modal
            open={open}
            onClose={() => {
              resetForm();
              onClose();
            }}
            title={t("buttonsText.filter")}
            primaryBtnProps={{
              type: "submit",
              text: t("buttonsText.apply"),
              loading: isSubmitting || submitting,
              disabled: shouldDisableApply,
              onClick: handleSubmit as any
            }}
            secondaryBtnProps={{
              text: t("buttonsText.cancel"),
              onClick: () => {
                resetForm();
                onClose();
              }
            }}
            theme={theme}
            size="customSize"
          >
            <Stack spacing={2}>
              <UserFilter
                name="userUniqueId"
                value={values.userUniqueId}
                onChange={(value: any) => setFieldValue("userUniqueId", value)}
                label={t("driverName.label", { ns: "formFieldsTranslation" })}
                placeholder={t("driverName.placeholder", {
                  ns: "formFieldsTranslation"
                })}
              />
              <MileageObjectFilter
                name="objectUniqueId"
                value={values.objectUniqueId}
                onChange={(value: any) =>
                  setFieldValue("objectUniqueId", value)
                }
                label={t("objectName.label", { ns: "formFieldsTranslation" })}
                placeholder={t("objectName.placeholder", {
                  ns: "formFieldsTranslation"
                })}
              />
            </Stack>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default MileageDeviationFilterModal;
